import React, { useState, useEffect, useMemo } from 'react';
import { styled } from '@mui/material/styles';
import { 
  Modal, 
  Box, 
  Typography, 
  Button, 
  Checkbox, 
  FormControlLabel, 
  MenuItem, 
  Select, 
  FormControl, 
  useMediaQuery, 
  useTheme,
  Switch,
  Stack
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PracticeModeIcon from '../../assets/icons/practice-mode.svg';
import SimulationModeIcon from '../../assets/icons/simulation-mode.svg';
import SpeakingSimulationPreview from '../../assets/images/ielts-speaking-simulation-preview.png';
import SubscriptionAiPromptDialog from './SubscriptionAiPromptDialog';

const SECTIONAL_TEST_MODE_KEY = 'ielts-sectional-test-mode';
const SIMULATION_WARNING = 'Choose Simulation only when you are ready for the full timed test. You cannot submit before time finishes. If you may need to submit early, choose Practice mode.';
const LISTENING_SIMULATION_WARNING = 'Choose Simulation only when you are ready for the full timed test. You cannot submit before time finishes. In Listening, audio cannot be paused, replayed, skipped, or moved from the bar. If you may need to submit early or control audio, choose Practice mode.';

const ModalContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '1200px',
  backgroundColor: 'white',
  borderRadius: '12px',
  boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.1)',
  padding: '24px',
  outline: 'none',
  [theme.breakpoints.down('lg')]: {
    maxWidth: '900px',
    padding: '22px',
  },
  [theme.breakpoints.down('md')]: {
    maxWidth: '700px',
    padding: '20px',
    width: '92%',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px 12px',
    width: '85%',
  },
  [theme.breakpoints.down('xs')]: {
    padding: '12px 10px',
    width: '80%',
  },
}));

const ModalTitle = styled(Typography)(({ theme }) => ({
  fontSize: '32px',
  fontWeight: '500',
  color: '#996CFE',
  textAlign: 'center',
  marginBottom: '24px',
  [theme.breakpoints.down('lg')]: {
    fontSize: '30px',
    marginBottom: '22px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '28px',
    marginBottom: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '24px',
    marginBottom: '16px',
  },
  [theme.breakpoints.down('xs')]: {
    fontSize: '20px',
    marginBottom: '14px',
  },
}));

const CloseButton = styled(Button)(({ theme }) => ({
  position: 'absolute',
  top: '3px',
  right: '1px',
  minWidth: 'auto',
  padding: '4px',
  color: '#666',
  [theme.breakpoints.down('sm')]: {
    top: '8px',
    right: '8px',
  },
}));

const ModesContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: '24px',
  [theme.breakpoints.down('lg')]: {
    gap: '20px',
  },
  [theme.breakpoints.down('md')]: {
    gap: '16px',
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: '16px',
  },
}));

const ModeBox = styled(Box)(({ theme }) => ({
  flex: 1,
  border: '1px solid #E0E0E0',
  borderRadius: '8px',
  padding: '24px',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: '#FFFFFF',
  [theme.breakpoints.down('lg')]: {
    padding: '22px',
  },
  [theme.breakpoints.down('md')]: {
    padding: '20px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '16px',
  },
  [theme.breakpoints.down('xs')]: {
    padding: '12px',
  },
}));

const ModeTitle = styled(Typography)(({ theme }) => ({
  fontSize: '24px',
  fontWeight: '500',
  color: '#333',
  marginBottom: '16px',
  textAlign: 'center',
  [theme.breakpoints.down('lg')]: {
    fontSize: '22px',
    marginBottom: '14px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '20px',
    marginBottom: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '18px',
    marginBottom: '10px',
  },
  [theme.breakpoints.down('xs')]: {
    fontSize: '16px',
    marginBottom: '8px',
  },
}));

const ModeIcon = styled('img')(({ theme }) => ({
  width: '80px',
  height: '80px',
  marginBottom: '16px',
  alignSelf: 'center',
  [theme.breakpoints.down('lg')]: {
    width: '70px',
    height: '70px',
    marginBottom: '14px',
  },
  [theme.breakpoints.down('md')]: {
    width: '60px',
    height: '60px',
    marginBottom: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '50px',
    height: '50px',
    marginBottom: '10px',
  },
  [theme.breakpoints.down('xs')]: {
    width: '40px',
    height: '40px',
    marginBottom: '8px',
  },
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontSize: '16px',
  fontWeight: 500,
  color: '#333',
  marginBottom: '12px',
  [theme.breakpoints.down('lg')]: {
    fontSize: '15px',
    marginBottom: '10px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '14px',
    marginBottom: '8px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
    marginBottom: '6px',
  },
}));

const InfoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: '16px',
  [theme.breakpoints.down('lg')]: {
    marginBottom: '14px',
  },
  [theme.breakpoints.down('md')]: {
    marginBottom: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: '10px',
  },
}));

const InfoIcon = styled(Box)(({ theme }) => ({
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: '#F8F2FF',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginRight: '8px',
  marginTop: '2px',
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    width: '20px',
    height: '20px',
    marginRight: '6px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '18px',
    height: '18px',
    marginRight: '5px',
  },
}));

const InfoText = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#666',
  [theme.breakpoints.down('lg')]: {
    fontSize: '13px',
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
  },
}));

const SimulationWarning = styled(Typography)(({ theme }) => ({
  color: '#B42318',
  backgroundColor: '#FFF1F0',
  border: '1px solid #FDA29B',
  borderRadius: '8px',
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: 1.45,
  padding: '10px 12px',
  marginBottom: '16px',
  [theme.breakpoints.down('md')]: {
    fontSize: '12px',
    padding: '9px 10px',
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: '11px',
    padding: '8px 9px',
  },
}));

const SimulationPreview = styled('img')(({ theme }) => ({
  width: '100%',
  maxWidth: '320px',
  aspectRatio: '16 / 9',
  objectFit: 'cover',
  alignSelf: 'center',
  border: '1px solid #E4D9FF',
  borderRadius: '6px',
  boxShadow: '0 8px 20px rgba(45, 28, 82, 0.12)',
  marginBottom: '16px',
  [theme.breakpoints.down('md')]: {
    maxWidth: '280px',
    marginBottom: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%',
  },
}));

const StyledCheckbox = styled(Checkbox)(({ theme }) => ({
  color: '#996CFE',
  '&.Mui-checked': {
    color: '#996CFE',
  },
  padding: '2px 8px 2px 0',
  [theme.breakpoints.down('md')]: {
    padding: '1px 6px 1px 0',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '0px 4px 0px 0',
  },
}));

const StyledFormControlLabel = styled(FormControlLabel)(({ theme }) => ({
  marginBottom: '8px',
  display: 'block',
  '& .MuiFormControlLabel-label': {
    fontSize: '14px',
    [theme.breakpoints.down('lg')]: {
      fontSize: '13px',
    },
    [theme.breakpoints.down('md')]: {
      fontSize: '12px',
    },
    [theme.breakpoints.down('sm')]: {
      fontSize: '11px',
    },
  },
}));

const TimeSelectContainer = styled(Box)(({ theme }) => ({
  marginBottom: '24px',
  [theme.breakpoints.down('lg')]: {
    marginBottom: '20px',
  },
  [theme.breakpoints.down('md')]: {
    marginBottom: '16px',
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: '12px',
  },
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  height: '40px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#E0E0E0',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#996CFE',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#996CFE',
  },
  [theme.breakpoints.down('md')]: {
    height: '36px',
    '& .MuiSelect-select': {
      fontSize: '13px',
      padding: '8px 14px',
    },
  },
  [theme.breakpoints.down('sm')]: {
    height: '32px',
    '& .MuiSelect-select': {
      fontSize: '12px',
      padding: '6px 12px',
    },
  },
}));

const StartButton = styled(Button)(({ theme }) => ({
  backgroundColor: '#996CFE',
  color: 'white',
  borderRadius: '8px',
  padding: '8px 24px',
  fontSize: '16px',
  textTransform: 'none',
  alignSelf: 'center',
  marginTop: 'auto',
  '&:hover': {
    backgroundColor: '#7C4DFF',
  },
  [theme.breakpoints.down('lg')]: {
    padding: '7px 22px',
    fontSize: '15px',
  },
  [theme.breakpoints.down('md')]: {
    padding: '6px 20px',
    fontSize: '14px',
  },
  [theme.breakpoints.down('sm')]: {
    padding: '5px 16px',
    fontSize: '13px',
  },
  [theme.breakpoints.down('xs')]: {
    padding: '4px 14px',
    fontSize: '12px',
  },
}));

const ToggleContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '16px',
}));

const ToggleLabel = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: '#333',
  [theme.breakpoints.down('sm')]: {
    fontSize: '13px',
  },
}));

const StyledSwitch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: '#996CFE',
    '&:hover': {
      backgroundColor: 'rgba(153, 108, 254, 0.08)',
    },
  },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
    backgroundColor: '#996CFE',
  },
}));

const TestModeModal = ({ open, onClose, onStartPracticeMode, onStartSimulationMode, numParts = 4, defaultTime = 30, timeOptions = [30, 25, 20, 'Unlimited'], partLabels, fullPartsLabel, testCategoryName, hideSimulationMode = false, requiresSubscriptionForSimulation = false, initialPartNumbers, initialMinutes }) => {
  const theme = useTheme();
  const isXSmall = useMediaQuery(theme.breakpoints.down('xs'));
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isMedium = useMediaQuery(theme.breakpoints.down('md'));
  const isLarge = useMediaQuery(theme.breakpoints.down('lg'));
  
  // State to track which mode is selected in mobile view
  const [mobileMode, setMobileMode] = useState('practice'); // 'practice' or 'simulation'
  // State for practice mode selections
  const defaultParts = useMemo(() => Array.from({ length: numParts }, (_, i) => i + 1), [numParts]);
  const [selectedParts, setSelectedParts] = useState(defaultParts);
  const practiceTime = Number.isInteger(initialMinutes) && initialMinutes > 0 && initialMinutes <= 240 ? initialMinutes : defaultTime;
  const practiceTimeOptions = timeOptions.includes(practiceTime) ? timeOptions : [practiceTime, ...timeOptions];
  const [selectedTime, setSelectedTime] = useState(practiceTime);
  const [subscriptionPromptOpen, setSubscriptionPromptOpen] = useState(false);
  const isListeningTest = String(testCategoryName || '').toLowerCase().includes('listening');
  const isSpeakingTest = String(testCategoryName || '').toLowerCase().includes('speaking');
  const isSimulationLocked = isSpeakingTest && requiresSubscriptionForSimulation;
  const simulationWarning = isListeningTest ? LISTENING_SIMULATION_WARNING : SIMULATION_WARNING;
  const simulationTitle = isSpeakingTest ? 'Examiner simulation' : 'Simulation test mode';
  const simulationDescription = isSpeakingTest
    ? 'Practice with a video examiner and a Zoom-like real exam flow.'
    : 'Simulation test mode is the best option to experience the real IELTS on web.';

  // Reset selectedParts when modal is opened (fixes leftover state)
  useEffect(() => {
    if (open) {
      setSelectedParts(Array.isArray(initialPartNumbers)
        ? defaultParts.filter((part) => initialPartNumbers.includes(part))
        : defaultParts);
      setSelectedTime(practiceTime);
      setMobileMode('practice');
    }
  }, [open, defaultParts, practiceTime, initialPartNumbers]);

  const rememberMode = (mode) => {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(SECTIONAL_TEST_MODE_KEY, mode);
    }
  };

  // Checkbox handlers
  const handlePartChange = (part) => (event) => {
    if (event.target.checked) {
      setSelectedParts(prev => [...prev, part].sort());
    } else {
      setSelectedParts(prev => prev.filter(p => p !== part));
    }
  };
  // Full parts handler
  const handleFullPartsChange = (event) => {
    if (event.target.checked) {
      setSelectedParts(defaultParts);
    } else {
      setSelectedParts([]);
    }
  };
  // Time select handler
  const handleTimeChange = (event) => {
    const value = event.target.value;
    setSelectedTime(value === 'Unlimited' ? 'Unlimited' : Number(value));
  };
  // Start Practice Mode handler
  const handleStartPractice = () => {
    rememberMode('practice');
    onStartPracticeMode(selectedParts, selectedTime);
  };

  const handleStartSimulation = () => {
    if (isSimulationLocked) {
      setSubscriptionPromptOpen(true);
      return;
    }

    rememberMode('simulation');
    onStartSimulationMode();
  };

  const getIconSize = () => {
    if (isXSmall) return "small";
    if (isSmall) return "small";
    return "medium";
  };

  const getCheckboxSize = () => {
    if (isXSmall) return "small";
    if (isSmall) return "small";
    return "medium";
  };

  const handleToggleChange = (event) => {
    setMobileMode(event.target.checked ? 'simulation' : 'practice');
  };

  // Render the desktop view with both modes side by side
  const renderDesktopView = () => (
    <ModesContainer>
      {/* Practice Mode */}
      <ModeBox>
        <ModeIcon src={PracticeModeIcon} alt="Practice Mode" />
        <ModeTitle>Practice mode</ModeTitle>
        
        <InfoContainer>
          <InfoIcon>
            <Typography sx={{ 
              fontSize: isXSmall ? '10px' : isSmall ? '11px' : isMedium ? '12px' : '14px', 
              fontWeight: 'bold', 
              color: '#996CFE' 
            }}>i</Typography>
          </InfoIcon>
          <InfoText>
            Practice mode is suitable for improving accuracy
            and time spent on each part.
          </InfoText>
        </InfoContainer>
        
        <SectionTitle>
          1. Choose part/task(s) you want to practice:
        </SectionTitle>
        
        <StyledFormControlLabel 
          control={<StyledCheckbox checked={selectedParts.length === numParts} onChange={handleFullPartsChange} size={getCheckboxSize()} />} 
          label={fullPartsLabel || `Full parts (${numParts} parts)`} 
        />
        {defaultParts.map((part, idx) => (
        <StyledFormControlLabel 
            key={part}
            control={<StyledCheckbox checked={selectedParts.includes(part)} onChange={handlePartChange(part)} size={getCheckboxSize()} />}
            label={partLabels ? partLabels[idx] : `Part ${part} (10 questions)`}
        />
        ))}
        
        <TimeSelectContainer>
          <SectionTitle>
            2. Choose a time limit:
          </SectionTitle>
          <FormControl fullWidth>
            <StyledSelect
              value={selectedTime}
              onChange={handleTimeChange}
              size={isSmall ? "small" : "medium"}
              displayEmpty
              renderValue={(value) => value === 'Unlimited' ? 'Unlimited' : `${value} mins`}
            >
              {practiceTimeOptions.map((t) => (
                <MenuItem key={t} value={t}>{t === 'Unlimited' ? 'Unlimited' : `${t} mins`}</MenuItem>
              ))}
            </StyledSelect>
          </FormControl>
        </TimeSelectContainer>
        
        <StartButton onClick={handleStartPractice} disabled={selectedParts.length === 0}>
          Start
        </StartButton>
      </ModeBox>
      
      {/* Simulation Test Mode */}
      {!hideSimulationMode && (
      <ModeBox>
        <ModeIcon src={SimulationModeIcon} alt="Simulation Mode" />
        <ModeTitle>{simulationTitle}</ModeTitle>
        
        <InfoContainer>
          <InfoIcon>
            <Typography sx={{ 
              fontSize: isXSmall ? '10px' : isSmall ? '11px' : isMedium ? '12px' : '14px', 
              fontWeight: 'bold', 
              color: '#996CFE' 
            }}>i</Typography>
          </InfoIcon>
          <InfoText>{simulationDescription}</InfoText>
        </InfoContainer>

        {isSpeakingTest && (
          <SimulationPreview
            src={SpeakingSimulationPreview}
            alt="IELTS speaking examiner simulation preview"
          />
        )}
        
        <SimulationWarning>
          {simulationWarning}
        </SimulationWarning>
        
        <SectionTitle>
          Test information
        </SectionTitle>
        <Typography sx={{ 
          fontSize: isXSmall ? '10px' : isSmall ? '11px' : isMedium ? '12px' : '14px', 
          color: '#666', 
          mb: isSmall ? 2 : 3 
        }}>
          {fullPartsLabel || `Full parts (${defaultTime} minutes - ${numParts} parts)`}
        </Typography>
        
        <StartButton onClick={handleStartSimulation} sx={{ mt: 'auto' }}>
          {isSimulationLocked ? 'Upgrade to use simulation' : 'Start'}
        </StartButton>
      </ModeBox>
      )}
    </ModesContainer>
  );

  // Render the mobile view with toggle switch
  const renderMobileView = () => (
    <>
      {!hideSimulationMode && (
      <ToggleContainer>
        <ToggleLabel sx={{ color: mobileMode === 'practice' ? '#996CFE' : '#666' }}>
          Practice
        </ToggleLabel>
        <StyledSwitch 
          checked={mobileMode === 'simulation'} 
          onChange={handleToggleChange}
        />
        <ToggleLabel sx={{ color: mobileMode === 'simulation' ? '#996CFE' : '#666' }}>
          Simulation
        </ToggleLabel>
      </ToggleContainer>
      )}

      {hideSimulationMode || mobileMode === 'practice' ? (
        // Practice Mode Content
        <ModeBox>
          <ModeIcon src={PracticeModeIcon} alt="Practice Mode" />
          <ModeTitle>Practice mode</ModeTitle>
          
          <InfoContainer>
            <InfoIcon>
              <Typography sx={{ 
                fontSize: isXSmall ? '10px' : isSmall ? '11px' : isMedium ? '12px' : '14px', 
                fontWeight: 'bold', 
                color: '#996CFE' 
              }}>i</Typography>
            </InfoIcon>
            <InfoText>
              Practice mode is suitable for improving accuracy
              and time spent on each part.
            </InfoText>
          </InfoContainer>
          
          <SectionTitle>
            1. Choose part/task(s) you want to practice:
          </SectionTitle>
          
          <StyledFormControlLabel 
            control={<StyledCheckbox checked={selectedParts.length === numParts} onChange={handleFullPartsChange} size={getCheckboxSize()} />} 
            label={fullPartsLabel || `Full parts (${numParts} parts)`} 
          />
          {defaultParts.map((part, idx) => (
          <StyledFormControlLabel 
              key={part}
              control={<StyledCheckbox checked={selectedParts.includes(part)} onChange={handlePartChange(part)} size={getCheckboxSize()} />}
              label={partLabels ? partLabels[idx] : `Part ${part} (10 questions)`}
          />
          ))}
          
          <TimeSelectContainer>
            <SectionTitle>
              2. Choose a time limit:
            </SectionTitle>
            <FormControl fullWidth>
              <StyledSelect
                value={selectedTime}
                onChange={handleTimeChange}
                size="small"
                displayEmpty
                renderValue={(value) => value === 'Unlimited' ? 'Unlimited' : `${value} mins`}
              >
                {practiceTimeOptions.map((t) => (
                  <MenuItem key={t} value={t}>{t === 'Unlimited' ? 'Unlimited' : `${t} mins`}</MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </TimeSelectContainer>
          
          <StartButton onClick={handleStartPractice} disabled={selectedParts.length === 0}>
            Start
          </StartButton>
        </ModeBox>
      ) : (
        // Simulation Mode Content
        <ModeBox>
          <ModeIcon src={SimulationModeIcon} alt="Simulation Mode" />
          <ModeTitle>{simulationTitle}</ModeTitle>
          
          <InfoContainer>
            <InfoIcon>
              <Typography sx={{ 
                fontSize: isXSmall ? '10px' : isSmall ? '11px' : isMedium ? '12px' : '14px', 
                fontWeight: 'bold', 
                color: '#996CFE' 
              }}>i</Typography>
            </InfoIcon>
            <InfoText>{simulationDescription}</InfoText>
          </InfoContainer>

          {isSpeakingTest && (
            <SimulationPreview
              src={SpeakingSimulationPreview}
              alt="IELTS speaking examiner simulation preview"
            />
          )}
          
          <SimulationWarning>
            {simulationWarning}
          </SimulationWarning>
          
          <SectionTitle>
            Test information
          </SectionTitle>
          <Typography sx={{ 
            fontSize: isXSmall ? '10px' : isSmall ? '11px' : isMedium ? '12px' : '14px', 
            color: '#666', 
            mb: 3 
          }}>
            {fullPartsLabel || `Full parts (${defaultTime} minutes - ${numParts} parts)`}
          </Typography>
          
          <StartButton onClick={handleStartSimulation} sx={{ mt: 'auto' }}>
            {isSimulationLocked ? 'Upgrade to use simulation' : 'Start'}
          </StartButton>
        </ModeBox>
      )}
    </>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="test-mode-modal"
      aria-describedby="choose-test-mode"
    >
      <ModalContainer>
        <CloseButton onClick={onClose}>
          <CloseIcon fontSize={getIconSize()} />
        </CloseButton>
        
        
        {isSmall ? renderMobileView() : renderDesktopView()}
        <SubscriptionAiPromptDialog
          open={subscriptionPromptOpen}
          onClose={() => setSubscriptionPromptOpen(false)}
          skillName="Speaking simulation"
        />
      </ModalContainer>
    </Modal>
  );
};

export default TestModeModal; 
